import { useEffect, useRef } from 'react';
import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { EditorState, Compartment, type Extension } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLine } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { json } from '@codemirror/lang-json';
import { bracketMatching, foldGutter, foldKeymap } from '@codemirror/language';
import { search, searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { useAppStore } from '@/stores/appStore';
import { createFluentTheme } from '@/components/WorkflowDesigner/CodeEditor/extensions';

const useStyles = makeStyles({
  editorContainer: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    overflow: 'hidden',
    '& .cm-editor': {
      flex: 1,
      width: '100%',
      height: '100%',
    },
    '& .cm-scroller': {
      fontFamily: tokens.fontFamilyMonospace,
      fontSize: tokens.fontSizeBase300,
    },
  },
});

export interface NodeCodeViewProps {
  /** The serialized node data to display. */
  value: unknown;
  /** Accessible label for the editor. */
  ariaLabel?: string;
}

/**
 * Read-only JSON code editor for the node editor's "Code" tab.
 * Provides syntax highlighting, line numbers, folding, and search.
 */
export const NodeCodeView = ({ value, ariaLabel = 'Node JSON' }: NodeCodeViewProps) => {
  const styles = useStyles();
  const theme = useAppStore((s) => s.theme);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const themeCompartment = useRef(new Compartment());

  const docString = (() => {
    try {
      return JSON.stringify(value ?? {}, null, 2);
    } catch {
      return String(value ?? '');
    }
  })();
  const docStringRef = useRef(docString);
  docStringRef.current = docString;

  // Create editor on mount
  useEffect(() => {
    if (!containerRef.current) return;

    const extensions: Extension[] = [
      lineNumbers(),
      history(),
      foldGutter({ openText: '▼', closedText: '▶' }),
      bracketMatching(),
      highlightActiveLine(),
      highlightSelectionMatches(),
      search({ top: true }),
      json(),
      EditorState.tabSize.of(2),
      // Read-only via state (blocks edits) but keep `editable: true` so the
      // editor's contentDOM remains focusable. Without focus, key bindings —
      // including search (Mod-f) — never fire.
      EditorState.readOnly.of(true),
      EditorView.contentAttributes.of({ 'aria-label': ariaLabel, 'aria-readonly': 'true' }),
      keymap.of([
        ...defaultKeymap,
        ...historyKeymap,
        ...foldKeymap,
        ...searchKeymap,
      ]),
      themeCompartment.current.of(createFluentTheme(theme)),
    ];

    const state = EditorState.create({
      doc: docStringRef.current,
      extensions,
    });

    const view = new EditorView({ state, parent: containerRef.current });
    viewRef.current = view;

    // Auto-focus the editor so Mod-A / Mod-F / arrow keys stay inside the
    // code view instead of falling through to the global canvas keyboard
    // handler (which would select all workflow nodes).
    view.focus();

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Recreate the editor when ariaLabel changes so CodeMirror's content attributes stay accurate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ariaLabel]);

  // Sync theme changes
  useEffect(() => {
    if (!viewRef.current) return;
    viewRef.current.dispatch({
      effects: themeCompartment.current.reconfigure(createFluentTheme(theme)),
    });
  }, [theme]);

  // Sync document when serialized value changes (preserve scroll position)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current === docString) return;

    const scrollTop = view.scrollDOM.scrollTop;
    const scrollLeft = view.scrollDOM.scrollLeft;
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: docString },
      scrollIntoView: false,
    });
    view.scrollDOM.scrollTop = scrollTop;
    view.scrollDOM.scrollLeft = scrollLeft;
  }, [docString]);

  return <div ref={containerRef} className={styles.editorContainer} />;
};
